import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { DataSearchComponent, SearchInstancesManagerService } from '@anecdotes/design-system/data-manipulation';
import { VirtualScrollerComponent } from '@iharbeck/ngx-virtual-scroller';
import { ReplaySubject, switchMap, tap } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { GridRow, GridView, SortDirection } from '../../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isValidDate } from '@anecdotes/utils';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GridComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  private readonly dataSearchInitialization$ = new ReplaySubject<(dataSearch: DataSearchComponent) => void>(1);

  private readonly displayedRowsInitialization$ = new ReplaySubject<void>(1);

  private currentSearchScopeKey: string;
  private columnHeaderToDataType: { [columnName: string]: 'date' | 'number' | 'string' } = {};

  @ViewChild('scrollContainer')
  private scrollContainer: ElementRef<HTMLDivElement>;

  @ViewChild('scroller')
  private scroller: VirtualScrollerComponent;

  private dataSearch: DataSearchComponent;

  sortedRows: GridRow[];

  displayedRows: GridRow[];

  currentSortDirection = SortDirection.ASC;

  sortedBy: string; // name of a field that the grid is sorted by

  expandingData: { rowIndex: number } = null;

  @Input()
  firstColumnCellTemplate: TemplateRef<any>;

  @Input()
  lastColumnCellTemplate: TemplateRef<any>;

  @Input()
  cellTemplate: TemplateRef<any>;

  @Input()
  firstColumnHeaderTemplate: TemplateRef<any>;

  @Input()
  lastColumnHeaderTemplate: TemplateRef<any>;

  @Input()
  headerTemplate: TemplateRef<any>;

  @Input()
  scopeKey: string;

  @Input()
  scrollDisabled: boolean;

  @Input()
  gridView: GridView;

  @Input()
  useVirtualScroll: boolean;

  @Input()
  sortEnabled: boolean;

  @Input()
  searchEnabled = true;

  @Input()
  headerClass: string;

  @Input()
  useAlternateBackground: boolean;

  @Input()
  scrollerAdditionalOffset = -10;

  @Input()
  shouldBeSortedByRowId?: number;

  @Input()
  isMultiselectEnabled: boolean;

  @Input()
  columnDefaultMaxWidth: number;

  @Input()
  selectedRowIds: string[];

  @Output()
  sortedByColumn = new EventEmitter<{ headerName: string; direction: string }>();

  get parentScroll(): HTMLElement {
    return this.scrollContainer?.nativeElement || this.elementRef?.nativeElement;
  }

  get firstFixedHeader(): string {
    return this.gridView.headersMetadata
      ? Object.keys(this.gridView.headersMetadata)?.find(
        (header) => this.gridView.headersMetadata[header].fixedPosition !== undefined,
      )
      : null;
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly searchInstancesManagerService: SearchInstancesManagerService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.executeDataSearch(() => this.displayedRowsInitialization$.next());

    this.searchInstancesManagerService
      .getSearchResultsPaginator(this.currentSearchScopeKey)
      .pipe(
        filter(Boolean),
        switchMap((searchPaginator) => searchPaginator.dataFocusChange),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((row) => {
        this.scroller.scrollInto(
          this.scroller.items.find((item) => item.rowId === row.rowId),
          undefined,
          row?.scrollerAdditionalOffset || this.scrollerAdditionalOffset,
          0
        );
      });

    this.dataSearchInitialization$
      .pipe(
        switchMap((callback) => {
          return this.searchInstancesManagerService.getDataSearch(this.currentSearchScopeKey)
            .pipe(
              filter(Boolean),
              take(1),
              tap((dataSearch) => {
                this.dataSearch = dataSearch;
                callback(dataSearch);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.displayedRowsInitialization$
      .pipe(
        switchMap(() => this.dataSearch.search),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data: GridRow[]) => {
        this.displayedRows = data;
        this.cd.detectChanges();
      });
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if ('gridView' in simpleChanges && this.gridView) {
      if (!this.searchEnabled) {
        this.displayedRows = [...this.gridView.rows];
        return;
      }
      this.executeDataSearch((dataSearch) => (dataSearch.searchDefinitions = this.gridView.searchDefinitions));

      if (this.shouldBeSortedByRowId) {
        this.sortBy(this.sortedBy || this.gridView.header[this.shouldBeSortedByRowId-1], this.currentSortDirection || SortDirection.ASC);
        this.displayedRows = this.sortedRows;
      } else {
        this.displayedRows = [...this.gridView.rows];
      }
    }
  }

  shouldShowMore(rowIndex: number): void {
    this.expandingData = { rowIndex };
  }

  searchableRowTrackBy(row: GridRow): number {
    return row.rowId;
  }

  rowTrackBy(_, row: GridRow): string {
    return row.rowId;
  }

  headerTrackBy(_, header: string): string {
    return header;
  }

  createCellId(rowIndex: number, cellIndex: number): string {
    return `cell-${rowIndex}-${cellIndex}`;
  }

  isColumnFixed(header: string): boolean {
    return this.gridView.headersMetadata && this.gridView.headersMetadata[header]?.fixedPosition !== undefined;
  }

  getColumnFixedPosition(header: string): number {
    return this.gridView.headersMetadata ? this.gridView.headersMetadata[header]?.fixedPosition : null;
  }

  fixedWidth(header: string): number {
    if (this.gridView.headersMetadata) {
      return this.gridView.headersMetadata[header]?.widthPx;
    }
  }

  fixeMaxdWidth(header: string): number {
    if (this.gridView.headersMetadata) {
      return this.gridView.headersMetadata[header]?.maxWidthPx || this.columnDefaultMaxWidth;
    }
  }

  isColumnHidden(header: string): boolean {
    return this.gridView.headersMetadata && this.gridView.headersMetadata[header]?.hidden;
  }

  createHeaderCellId(headerCellIndex: number): string {
    return `headercell-${headerCellIndex}`;
  }

  getRowIndexById(rowId: string): number {
    return this.displayedRows.findIndex((row) => row.rowId === rowId);
  }

  resolveOriginalId(row: GridRow): number {
    return (row?.originalrowIdentifier === undefined || row?.originalrowIdentifier === null) ? row.rowId : row.originalrowIdentifier;
  }

  columnHeaderClick(headerValue: string): void {
    if (this.sortEnabled) {
      if (this.sortedBy === headerValue) {
        switch (this.currentSortDirection) {
          case SortDirection.ASC: {
            this.currentSortDirection = SortDirection.DESC;
            break;
          }
          default: {
            this.currentSortDirection = SortDirection.ASC;
          }
        }
      } else {
        this.currentSortDirection = SortDirection.ASC;
      }

      this.sortBy(headerValue, this.currentSortDirection);
      this.sortedByColumn.emit({ headerName: headerValue, direction: this.currentSortDirection });
      this.cd.detectChanges();
    }
  }

  multiSelectValueChange(): void {
    this.cd.detectChanges();
  }

  isRowSelected(rowId: string): boolean {
    return this.selectedRowIds?.includes(rowId);
  }



  private determineColumnType(fieldName: string): 'date' | 'number' | 'string' {
    if (this.columnHeaderToDataType[fieldName]) {
      return this.columnHeaderToDataType[fieldName];
    }

    let calculatedColumnType: 'date' | 'number' | 'string';

    for (const row of this.gridView.rows) {
      const value = row.simplifiedCellsObject[fieldName]?.replace('|', '');
      if (value === undefined || value === null || value.trim() === '') {
        continue;
      }

      const paresded = Date.parse(value);
      if (isValidDate(paresded) && isNaN(Number(value))) {
        calculatedColumnType = 'date';
      } else if (!isNaN(Number(value)) && value.trim() !== '') {
        calculatedColumnType = 'number';
      } else {
        calculatedColumnType = 'string';
        break;
      }
    }

    this.columnHeaderToDataType[fieldName] = calculatedColumnType;

    return calculatedColumnType;
  }

  private sortBy(fieldName: string, sortDir: SortDirection): void {
    this.sortedBy = fieldName;
    const sortOrder = sortDir === SortDirection.DESC ? -1 : 1;
    const columnType = this.determineColumnType(fieldName);
    this.sortedRows = [...this.gridView.rows].sort((row1, row2) => {
      const value1 = row1.simplifiedCellsObject[fieldName];
      const value2 = row2.simplifiedCellsObject[fieldName];

      return this.compareValues(value1, value2, columnType, sortOrder);
    });
    this.displayedRows = [...this.sortedRows];
  }

  private compareValues(
    value1: any,
    value2: any,
    columnType: string,
    sortOrder: number
  ): number {
    switch (columnType) {
      case 'date':
        return this.compareDates(value1, value2, sortOrder);
      case 'number':
        return this.compareNumbers(value1, value2, sortOrder);
      case 'string':
      default:
        return this.compareStrings(value1, value2, sortOrder);
    }
  }

  private compareDates(value1: any, value2: any, sortOrder: number): number {
    const dateA = value1 ? Date.parse(value1.replace('|', '')) : NaN;
    const dateB = value2 ? Date.parse(value2.replace('|', '')) : NaN;

    return this.handleNaN(dateA, dateB, sortOrder) ?? (dateA - dateB) * sortOrder;
  }

  private compareNumbers(value1: any, value2: any, sortOrder: number): number {
    const numA = value1 !== undefined ? Number(value1) : NaN;
    const numB = value2 !== undefined ? Number(value2) : NaN;

    return this.handleNaN(numA, numB, sortOrder) ?? (numA - numB) * sortOrder;
  }

  private compareStrings(value1: any, value2: any, sortOrder: number): number {
    const isEmptyOrInvalid = (value: any): boolean => {
      const str = value !== undefined && value !== null ? String(value).trim() : '';
      return str === '' || value === null || value === undefined;
    };

    const strA = value1 !== undefined && value1 !== null ? String(value1).trim() : '';
    const strB = value2 !== undefined && value2 !== null ? String(value2).trim() : '';

    const isEmptyOrInvalidA = isEmptyOrInvalid(value1);
    const isEmptyOrInvalidB = isEmptyOrInvalid(value2);

    if (isEmptyOrInvalidA && isEmptyOrInvalidB) {
      return 0;
    }
    if (isEmptyOrInvalidA) {
      return sortOrder;
    }
    if (isEmptyOrInvalidB) {
      return -sortOrder;
    }

    return strA.localeCompare(strB, undefined, { numeric: true }) * sortOrder;
  }

  /**
   * Handles NaN values in comparison.
   * Returns null if both values are valid (not NaN), or a number (-1, 1, or 0) if one or both values are NaN.
   */
  private handleNaN(value1: number, value2: number, sortOrder: number): number | null {
    const isNaN1 = isNaN(value1);
    const isNaN2 = isNaN(value2);

    if (isNaN1 && isNaN2) {
      return 0;
    }
    if (isNaN1) {
      return sortOrder;
    }
    if (isNaN2) {
      return -sortOrder;
    }

    return null;
  }

  private executeDataSearch(callback: (dataSearch: DataSearchComponent) => void): void {
    if (!this.searchEnabled) {
      return;
    }
    if (!this.dataSearch) {
      if (!this.currentSearchScopeKey) {
        this.currentSearchScopeKey = this.searchInstancesManagerService.getSearchScopeKey(
          this.elementRef.nativeElement
        );
      }

      this.dataSearchInitialization$.next(callback);
    } else {
      callback(this.dataSearch);
    }
  }
}
